import React from 'react';

/**
 * ChunkErrorBoundary — catches the "lazy import landed on a stale chunk hash"
 * failure that happens when the SPA is redeployed while a tab is still open.
 *
 * Symptom: user clicks something that triggers a React.lazy() boundary,
 * Vite asks for /assets/PdfPreview-OLDHASH.js, server returns 404 (new
 * deploy has new hash), React.lazy promise rejects, <Suspense> fallback
 * sticks forever. Without an error boundary, the user has no recovery
 * path except a manual refresh — which they don't know to do, because
 * the fallback just shows "Loading viewer…".
 *
 * Catches:
 *   - Vite ChunkLoadError
 *   - Any error whose message matches "Failed to fetch dynamically imported module"
 *     or "Importing a module script failed" (Safari/Firefox phrasing)
 * Re-throws anything else so it surfaces in real error tracking.
 */
export default class ChunkErrorBoundary extends React.Component {
  state = { isChunkError: false };

  static getDerivedStateFromError(err) {
    const msg = String(err?.message || err || '');
    const isChunkError =
      err?.name === 'ChunkLoadError' ||
      /Failed to fetch dynamically imported module/i.test(msg) ||
      /Importing a module script failed/i.test(msg) ||
      /error loading dynamically imported module/i.test(msg);
    if (!isChunkError) throw err;
    return { isChunkError: true };
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.isChunkError) return this.props.children;
    return (
      <div
        style={{
          padding: 24,
          textAlign: 'center',
          color: 'var(--ink-soft, #555)',
          fontSize: '0.95rem',
          lineHeight: 1.5,
        }}
      >
        <p style={{ margin: '0 0 12px' }}>
          A new version of the app is available.
          <br />
          Refresh to open this document.
        </p>
        <button
          type="button"
          onClick={this.handleReload}
          style={{
            padding: '8px 18px',
            borderRadius: 6,
            border: '1px solid var(--hairline, #ccc)',
            background: 'var(--brand-blue, #1976d2)',
            color: '#fff',
            fontSize: '0.9rem',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Refresh
        </button>
      </div>
    );
  }
}
